import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import axios from "axios";
import useAuthStore from "./store/authStore";

// ── In-Memory LocalStorage/SessionStorage Mock ─────────────────────────────
const inMemoryStorage = {};
const originalLocalStorage = window.localStorage;

const authKeys = [
  "has_session",
  "refresh_token",
  "role",
  "employee_id",
  "full_name",
  "user",
  "must_change_password",
  "is_superuser"
];

Object.defineProperty(window, "localStorage", {
  value: {
    getItem(key) {
      if (authKeys.includes(key)) {
        return inMemoryStorage[key] || null;
      }
      return originalLocalStorage.getItem(key);
    },
    setItem(key, value) {
      if (authKeys.includes(key)) {
        inMemoryStorage[key] = String(value);
      } else {
        originalLocalStorage.setItem(key, value);
      }
    },
    removeItem(key) {
      if (authKeys.includes(key)) {
        delete inMemoryStorage[key];
      } else {
        originalLocalStorage.removeItem(key);
      }
    },
    clear() {
      for (const key of authKeys) {
        delete inMemoryStorage[key];
      }
      originalLocalStorage.clear();
    },
    key(index) {
      return originalLocalStorage.key(index);
    },
    get length() {
      return originalLocalStorage.length;
    }
  },
  writable: true
});

const inMemorySessionStorage = {};
const originalSessionStorage = window.sessionStorage;

Object.defineProperty(window, "sessionStorage", {
  value: {
    getItem(key) {
      if (authKeys.includes(key)) {
        return inMemorySessionStorage[key] || null;
      }
      return originalSessionStorage.getItem(key);
    },
    setItem(key, value) {
      if (authKeys.includes(key)) {
        inMemorySessionStorage[key] = String(value);
      } else {
        originalSessionStorage.setItem(key, value);
      }
    },
    removeItem(key) {
      if (authKeys.includes(key)) {
        delete inMemorySessionStorage[key];
      } else {
        originalSessionStorage.removeItem(key);
      }
    },
    clear() {
      for (const key of authKeys) {
        delete inMemorySessionStorage[key];
      }
      originalSessionStorage.clear();
    },
    key(index) {
      return originalSessionStorage.key(index);
    },
    get length() {
      return originalSessionStorage.length;
    }
  },
  writable: true
});

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <div className="min-h-screen bg-[#eaf2fb] flex items-center justify-center font-sans text-[#0f2a44] font-semibold text-lg">
    Loading Sahvia HRMS...
  </div>
);

const setAuthInMemory = (data) => {
  if (data.employee_id) {
    useAuthStore.getState().setAuth({
      token: "present",
      role: data.role,
      employeeId: data.employee_id,
      fullName: data.full_name || "",
      mustChangePassword: data.must_change_password === true || String(data.must_change_password) === "true",
      isSuperuser: data.is_superuser === true || String(data.is_superuser) === "true",
      user: {
        employee_id: data.employee_id,
        full_name: data.full_name || "",
        role: data.role
      }
    });
  }
};

const checkAuth = async () => {
  const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
  try {
    const res = await axios.get(`${apiUrl}/accounts/api/auth/me/`, { withCredentials: true });
    setAuthInMemory(res.data);
  } catch (err) {
    if (err.response?.status === 401) {
      try {
        await axios.post(`${apiUrl}/accounts/api/token/refresh/`, {}, { withCredentials: true });
        const resRetry = await axios.get(`${apiUrl}/accounts/api/auth/me/`, { withCredentials: true });
        setAuthInMemory(resRetry.data);
      } catch (refreshErr) {
        console.log("No active session or session expired:", refreshErr);
      }
    } else {
      console.log("Auth check error:", err);
    }
  } finally {
    root.render(
      <div className="min-h-screen bg-[#eaf2fb] font-sans">
        <App />
      </div>
    );
  }
};

checkAuth();
